/**
 * Patrol Feature Tab Constants
 * Material Design 3 - Theme, configuration, and styling constants
 */

// ═══════════════════════════════════════════════════════════════════════════
// THEME
// ═══════════════════════════════════════════════════════════════════════════

export const THEME = {
  colors: {
    primary: "#2932E5",
    /** Compare Patrol: session list viewport only (panel A = left, panel B = right). */
    comparePatrolSessionListLeft: "#CFF1D1",
    comparePatrolSessionListRight: "#CBF5F5",
    background: "#F2F2F2",
    bodyText: "#4A5565",
    heading: "#FF8555",
    activeState: "#FFE0D0",
    stroke: "#E5E7EB",
    sideMenu: "#212121",
    secondaryText: "#99a1af",
    surfaceVariant: "#FAFAFA",
  },
  spacing: {
    xs: 0.5, // 4px
    sm: 1, // 8px
    md: 1.5, // 12px
    lg: 2, // 16px
    xl: 3, // 24px
  },
  borderRadius: {
    small: "8px",
    medium: "12px",
  },
  typography: {
    cardTitle: {
      fontFamily: "Roboto, sans-serif",
      fontSize: "16px",
      fontWeight: 400,
      color: "#FF8555",
    },
    body: {
      fontFamily: "Roboto, sans-serif",
      fontSize: "14px",
      color: "#4A5565",
    },
    caption: {
      fontFamily: "Roboto, sans-serif",
      fontSize: "13px",
      fontWeight: 500,
      color: "#4A5565",
    },
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════

export const STYLES = {
  toggleButton: {
    default: {
      bgcolor: "transparent",
      color: THEME.colors.bodyText,
      borderColor: THEME.colors.stroke,
    },
    selected: {
      bgcolor: `${THEME.colors.activeState} !important`,
      color: `${THEME.colors.heading} !important`,
      borderColor: `${THEME.colors.heading} !important`,
    },
    hover: {
      bgcolor: "#FFD0B8 !important",
    },
  },
  accordion: {
    header: {
      bgcolor: THEME.colors.surfaceVariant,
      borderRadius: THEME.borderRadius.small,
      minHeight: "48px",
      hoverBgcolor: THEME.colors.activeState,
    },
    border: `1px solid ${THEME.colors.stroke}`,
    borderRadius: `${THEME.borderRadius.small} !important`,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// TABLE CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

export const TABLE_CONFIG = {
  cellPadding: {
    vertical: "8px",
    horizontal: "8px",
  },
  columns: {
    patrolId: {
      width: "56px",
      minWidth: "56px",
    },
    date: {
      width: "46px",
      minWidth: "46px",
    },
    path: {
      width: "28%",
      minWidth: "180px",
      maxChars: 55,
    },
    name: {
      width: "28%",
      minWidth: "180px",
      maxChars: 55,
    },
    session: {
      width: "72px",
      minWidth: "72px",
    },
    actions: {
      width: "56px",
      minWidth: "56px",
    },
  },
};

export const TABLE_CONFIG_2 = {
  cellPadding: { vertical: "8px", horizontal: "8px" },
  columns: {
    patrolId: {
      minWidth: 60,
      maxWidth: 140,
    },
    date: {
      width: 170,
      minWidth: 150,
    },
    path: {
      width: "35%",
    },
    name: {
      width: "35%",
    },
    session: {
      width: 80,
    },
  },
};

// export const TABLE_CONFIG_2 = {
//   cellPadding: {
//     vertical: "8px",
//     horizontal: "8px",
//   },
//   columns: {
//     patrolId: {
//       width: "56px",
//       minWidth: "56px",
//     },
//     date: {
//       width: "46px",
//       minWidth: "46px",
//     },
//     path: {
//       width: "28%",
//       minWidth: "180px",
//       maxChars: 55,
//     },
//     name: {
//       width: "28%",
//       minWidth: "180px",
//       maxChars: 55,
//     },
//     session: {
//       width: "72px",
//       minWidth: "72px",
//     },
//     actions: {
//       width: "56px",
//       minWidth: "56px",
//     },
//   },
// };

// ═══════════════════════════════════════════════════════════════════════════
// STORAGE KEYS
// ═══════════════════════════════════════════════════════════════════════════

export const STORAGE_KEYS = {
  panelWidth: "patrolFeatureTab_panelWidth",
  sortField: "patrolFeatureTab_sortField",
  sortDirection: "patrolFeatureTab_sortDirection",
  groupingMode: "patrolFeatureTab_groupingMode",
};

// ═══════════════════════════════════════════════════════════════════════════
// DATE FORMAT
// ═══════════════════════════════════════════════════════════════════════════

export const DATE_FORMAT = {
  locale: "en-US",
  monthFormat: "short",
};

// ═══════════════════════════════════════════════════════════════════════════
// SESSION FORMAT
// ═══════════════════════════════════════════════════════════════════════════

export const SESSION_FORMAT = {
  prefix: "S-",
  paddingLength: 3,
  paddingChar: "0",
};

// ═══════════════════════════════════════════════════════════════════════════
// PATROL PANORAMAS
// ═══════════════════════════════════════════════════════════════════════════

export const PATROL_PANORAMAS = {
  default: "/images/default-panorama.jpg",
};

// ═══════════════════════════════════════════════════════════════════════════
// CARD TITLE STYLE (re-exported for consistency)
// ═══════════════════════════════════════════════════════════════════════════

export const CARD_TITLE_STYLE = {
  fontFamily: "Roboto, sans-serif",
  fontSize: "16px",
  fontWeight: 400,
  color: "#ff8555",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

// ═══════════════════════════════════════════════════════════════════════════
// VIDEO PLAYER CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

export const VIDEO_PLAYER = {
  defaultVolume: 1.0,
  rotationStep: 15,
  maxRotation: 360,
};
